import type {
  ModalAvisoPagoProps,
  UseModalAvisoPagoReturn,
} from "./types";

const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;

const MENSAJES_USER_DATA = [
  "Hoy se vence la fecha de pago de su cuenta. Recuerde que tiene 3 dias a partir de hoy para realizar el pago.",
  "En caso de que no pague dentro de 3 dias su cuenta sera bloqueada y no podra acceder a su informacion.",
] as const;

const MENSAJES_USUARIO_RUTA = [
  "Hoy se vence la fecha de pago de su cuenta. Comuniquese con su administrador para realizar el pago.",
] as const;

const MENSAJES_VACIOS = [] as const;

const normalizarFecha = (value: unknown): Date | null => {
  const valueAsObject = typeof value === "object" && value !== null ? value : null;

  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (
    valueAsObject &&
    "toDate" in valueAsObject &&
    typeof valueAsObject.toDate === "function"
  ) {
    return valueAsObject.toDate();
  }

  if (
    valueAsObject &&
    "toMillis" in valueAsObject &&
    typeof valueAsObject.toMillis === "function"
  ) {
    return new Date(valueAsObject.toMillis());
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const fecha = new Date(value);

  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

const esMismoDia = (fecha1: Date, fecha2: Date): boolean =>
  fecha1.getDate() === fecha2.getDate() &&
  fecha1.getMonth() === fecha2.getMonth() &&
  fecha1.getFullYear() === fecha2.getFullYear();

const formatearDias = (dias: number): string => (dias === 1 ? "1 dia" : `${dias} dias`);

const construirMensajesAdmin = (proximoPago: unknown): string[] => {
  const fechaProximoPago = normalizarFecha(proximoPago);

  if (!fechaProximoPago) {
    return [...MENSAJES_USER_DATA];
  }

  const fechaHoy = new Date();

  if (esMismoDia(fechaProximoPago, fechaHoy)) {
    return [...MENSAJES_USER_DATA];
  }

  const diferenciaEnMillis = fechaHoy.getTime() - fechaProximoPago.getTime();
  const diasVencido = Math.max(
    Math.floor(diferenciaEnMillis / MILLISECONDS_IN_DAY),
    0
  );

  if (diasVencido <= 0) {
    return [...MENSAJES_USER_DATA];
  }

  const diasRestantes = Math.max(3 - diasVencido, 0);

  return [
    `La fecha de pago de su cuenta vencio hace ${formatearDias(diasVencido)}. Recuerde que tiene ${formatearDias(diasRestantes)} para realizar el pago.`,
    `En caso de que no pague dentro de ${formatearDias(diasRestantes)} su cuenta sera bloqueada y no podra acceder a su informacion.`,
  ];
};

export const useModalAvisoPago = ({
  setIsAvisoPago,
  userData,
  usuarioRuta,
}: ModalAvisoPagoProps): UseModalAvisoPagoReturn => {
  const handleClose = () => {
    setIsAvisoPago(false);
  };

  if (userData) {
    return {
      handleClose,
      mensajes: construirMensajesAdmin(userData.proximoPago),
    };
  }

  if (usuarioRuta) {
    return {
      handleClose,
      mensajes: MENSAJES_USUARIO_RUTA,
    };
  }

  return {
    handleClose,
    mensajes: MENSAJES_VACIOS,
  };
};
